import type {
  AttributeReviewApproveSelectedMutation,
  TaxonomyApprovalPipelineSubscription,
} from "@/graphql/generated";

export type ApprovalPipelineStatus =
  | "QUEUED"
  | "DISPATCHING"
  | "RUNNING"
  | "SUCCESS"
  | "FAILED"
  | "SKIPPED"
  | "CANCELLED";

export interface ApprovalPipelineFieldResult {
  inputIndex?: number | null;
  sourceFieldId: string;
  status: string;
  code?: string | null;
  message?: string | null;
}

export interface ApprovalPipelineValueResult {
  inputIndex?: number | null;
  sourceFieldId?: string | null;
  valueApprovalId?: string | null;
  canonicalPath?: string | null;
  marketplaceValue?: string | null;
  decision?: string | null;
  status: string;
  code?: string | null;
  message?: string | null;
  marketplaceSpecificKey?: string | null;
}

export interface ApprovalPipelineState {
  syncJobId: string | null;
  pipelineId?: string | null;
  mappingSessionId: string;
  status: ApprovalPipelineStatus | string;
  taxonomyStatus?: string | null;
  retryable: boolean;
  message?: string | null;
  error?: string | null;
  errorType?: string | null;
  errorDetails?: unknown;
  affected?: unknown;
  payload?: Record<string, unknown> | null;
  marketplaceId?: string | null;
  marketplace?: string | null;
  productType?: string | null;
  terminal: boolean;
  occurredAt?: string | null;
  fieldResults: ApprovalPipelineFieldResult[];
  valueResults: ApprovalPipelineValueResult[];
}

type ApproveSelectedPayload =
  AttributeReviewApproveSelectedMutation["attributeReviewApproveSelected"];
type PipelineUpdate =
  TaxonomyApprovalPipelineSubscription["taxonomyApprovalPipeline"];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function stringFromPayload(
  payload: Record<string, unknown> | null,
  keys: string[],
): string | null {
  for (const key of keys) {
    const value = asString(payload?.[key]);
    if (value) return value;
  }
  return null;
}

export function normalizeApprovalPipelineStatus(status: string): string {
  return status.trim().toUpperCase();
}

export function isApprovalPipelineTerminal(status: string): boolean {
  return ["SUCCESS", "FAILED", "SKIPPED", "CANCELLED"].includes(
    normalizeApprovalPipelineStatus(status),
  );
}

export function isApprovalPipelineActive(
  state?: Pick<ApprovalPipelineState, "status" | "terminal"> | null,
): boolean {
  if (!state) return false;
  if (state.terminal) return false;
  // DISPATCHING: the worker has claimed the job; the subscribe snapshot can carry it.
  return ["QUEUED", "DISPATCHING", "RUNNING"].includes(
    normalizeApprovalPipelineStatus(state.status),
  );
}

export function getApprovalPipelineStatusLabel(status: string): string {
  const normalized = normalizeApprovalPipelineStatus(status);
  if (normalized === "QUEUED") return "Approval queued";
  if (normalized === "RUNNING") return "Approval running";
  if (normalized === "SUCCESS") return "Approval complete";
  if (normalized === "FAILED") return "Approval failed";
  if (normalized === "SKIPPED") return "Approval skipped";
  if (normalized === "CANCELLED") return "Approval cancelled";
  return status.replaceAll("_", " ").toLowerCase();
}

function parseFieldResult(value: unknown): ApprovalPipelineFieldResult | null {
  const record = asRecord(value);
  if (!record) return null;
  const sourceFieldId =
    asString(record.sourceFieldId) ?? asString(record.source_field_id);
  const status = asString(record.status);
  if (!sourceFieldId || !status) return null;
  return {
    inputIndex: asNumber(record.inputIndex ?? record.input_index),
    sourceFieldId,
    status,
    code: asString(record.code),
    message: asString(record.message),
  };
}

function parseValueResult(value: unknown): ApprovalPipelineValueResult | null {
  const record = asRecord(value);
  if (!record) return null;
  const status = asString(record.status);
  if (!status) return null;
  return {
    inputIndex: asNumber(record.inputIndex ?? record.input_index),
    sourceFieldId:
      asString(record.sourceFieldId) ?? asString(record.source_field_id),
    valueApprovalId:
      asString(record.valueApprovalId) ?? asString(record.value_approval_id),
    canonicalPath:
      asString(record.canonicalPath) ?? asString(record.canonical_path),
    marketplaceValue:
      asString(record.marketplaceValue) ?? asString(record.marketplace_value),
    decision: asString(record.decision),
    status,
    code: asString(record.code),
    message: asString(record.message),
    marketplaceSpecificKey:
      asString(record.marketplaceSpecificKey) ??
      asString(record.marketplace_specific_key),
  };
}

export function parseApprovalPipelineFieldResults(
  value: unknown,
): ApprovalPipelineFieldResult[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const parsed = parseFieldResult(item);
    return parsed ? [parsed] : [];
  });
}

export function parseApprovalPipelineValueResults(
  value: unknown,
): ApprovalPipelineValueResult[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const parsed = parseValueResult(item);
    return parsed ? [parsed] : [];
  });
}

export function approvalValueResultKey(
  result: Pick<
    ApprovalPipelineValueResult,
    "sourceFieldId" | "valueApprovalId" | "marketplaceValue"
  >,
): string | null {
  if (result.valueApprovalId) return `approval:${result.valueApprovalId}`;
  if (result.sourceFieldId && result.marketplaceValue) {
    return `field:${result.sourceFieldId}\u0000value:${result.marketplaceValue}`;
  }
  if (result.marketplaceValue) return `value:${result.marketplaceValue}`;
  return null;
}

export function approvalPipelineStateFromMutation(
  payload: Pick<
    ApproveSelectedPayload,
    | "mappingSessionId"
    | "syncJobId"
    | "pipelineId"
    | "status"
    | "retryable"
    | "fieldResults"
    | "valueResults"
  >,
): ApprovalPipelineState {
  return {
    syncJobId: payload.syncJobId ?? null,
    pipelineId: payload.pipelineId ?? null,
    mappingSessionId: payload.mappingSessionId,
    status: payload.status,
    retryable: payload.retryable,
    message: null,
    terminal: !payload.syncJobId || isApprovalPipelineTerminal(payload.status),
    occurredAt: new Date().toISOString(),
    fieldResults: payload.fieldResults,
    valueResults: payload.valueResults,
  };
}

export function approvalPipelineStateFromUpdate(
  update: PipelineUpdate,
): ApprovalPipelineState {
  const payload = asRecord(update.payload);
  return {
    syncJobId: update.syncJobId,
    pipelineId: update.pipelineId ?? null,
    mappingSessionId: update.mappingSessionId,
    status: update.status,
    taxonomyStatus: update.taxonomyStatus ?? null,
    retryable: Boolean(update.retryable),
    message: update.message ?? null,
    error: update.error ?? null,
    errorType: update.errorType ?? null,
    errorDetails: update.errorDetails,
    affected: update.affected,
    payload,
    marketplaceId: stringFromPayload(payload, [
      "marketplaceId",
      "marketplace_id",
    ]),
    marketplace: stringFromPayload(payload, ["marketplace"]),
    productType: stringFromPayload(payload, ["productType", "product_type"]),
    terminal: update.terminal,
    occurredAt: update.occurredAt,
    fieldResults: parseApprovalPipelineFieldResults(update.fieldResults),
    valueResults: parseApprovalPipelineValueResults(update.valueResults),
  };
}

export function mergeApprovalPipelineState(
  previous: ApprovalPipelineState | null,
  next: ApprovalPipelineState,
): ApprovalPipelineState {
  if (!previous || previous.syncJobId !== next.syncJobId) return next;
  return {
    ...previous,
    ...next,
    retryable: next.retryable || previous.retryable,
    payload: next.payload ?? previous.payload,
    marketplaceId: next.marketplaceId ?? previous.marketplaceId,
    marketplace: next.marketplace ?? previous.marketplace,
    productType: next.productType ?? previous.productType,
    fieldResults:
      next.fieldResults.length > 0 ? next.fieldResults : previous.fieldResults,
    valueResults:
      next.valueResults.length > 0 ? next.valueResults : previous.valueResults,
  };
}

export function getApprovalPipelineFailureMessage(
  state: ApprovalPipelineState,
): string {
  return (
    state.error ||
    state.message ||
    (state.retryable
      ? "Approval failed, but this pipeline can be retried."
      : "Approval failed. Review the row errors before trying again.")
  );
}

export function resultMessage(result?: {
  status?: string | null;
  message?: string | null;
  code?: string | null;
}): string | null {
  if (!result) return null;
  if (result.message) return result.message;
  if (result.code) return result.code.replaceAll("_", " ").toLowerCase();
  if (result.status) return getApprovalPipelineStatusLabel(result.status);
  return null;
}

export function hasPipelineResults(value: unknown): boolean {
  if (!Array.isArray(value)) return false;
  return value.some(
    (item) => asRecord(item) && asString(asRecord(item)?.status),
  );
}

export function pipelineUpdateHasTerminalSuccess(
  state: Pick<ApprovalPipelineState, "status" | "terminal">,
): boolean {
  return (
    state.terminal &&
    normalizeApprovalPipelineStatus(state.status) === "SUCCESS"
  );
}

export function pipelineUpdateHasTerminalFailure(
  state: Pick<ApprovalPipelineState, "status" | "terminal">,
): boolean {
  return (
    state.terminal &&
    ["FAILED", "CANCELLED"].includes(
      normalizeApprovalPipelineStatus(state.status),
    )
  );
}

// True when an approval-pipeline terminal failure is the session-atomic
// "Missing value_decisions for pending registry value approvals" rejection.
// Detected via several signals because the failure shape varies: errorType,
export function statusFromMaybeBoolean(
  status: string,
  terminal?: boolean | null,
): { status: string; terminal: boolean } {
  return {
    status,
    terminal: asBoolean(terminal) ?? isApprovalPipelineTerminal(status),
  };
}
